import React, { useEffect, useState } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";
import { useParams, Navigate, useNavigate } from "react-router-dom";

const DIFFICULTY = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];


const getEmptyWorkoutStep = (masterWorkout = {}) => ({
  _id: null,
  workoutKey: masterWorkout.key || "",
  topic: "",
  title: "",
  summary: "",
  wisdomQuote: { text: "", author: "", source: "" },
  reflection: "",
  image: { url: "", caption: "" },
  actionTask: { title: "", instructions: "", difficulty: "easy", estimatedMinutes: 3 },
  capture: { enabled: true, saveKey: "", promptTitle: "", promptText: "", inputType: "text", choices: [] },
  points: { completed: 10, slipped: 3, skipped: 0 },
  badgeTag: "",
  responseButtons: [
    { label: "Completed", value: "completed", nudgeMessage: "Amazing — keep going 🔥" },
    { label: "Slipped", value: "slipped", nudgeMessage: "It’s okay — slipping is part of learning 💛" },
    { label: "Skipped", value: "skipped", nudgeMessage: "No stress — come back when you're ready 🌱" },
  ],
  lastStep: false,
  isActive: true,
});

export const AdminBuildWorkout = () => {
  const { admin } = useAdminAuthContext();
  const { id } = useParams();
  const navigate = useNavigate();

  const [masterWorkout, setMasterWorkout] = useState(null);
  const [workoutSteps, setWorkoutSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editingStep, setEditingStep] = useState(null);
  const [form, setForm] = useState(getEmptyWorkoutStep());


const [imageFile, setImageFile] = useState(null);
const [imagePreview, setImagePreview] = useState("");

  const fetchMasterWorkout = async () => {
    try {
      if (!id || !admin?.token) return;
      setLoading(true);
      setError("");

      const res = await fetch(`${API_URL.ADMIN_GET_WORKOUT_BY_ID}${id}`, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to fetch master workout");
      setMasterWorkout(data.workout || {});

      const stepsRes = await fetch(`${API_URL.ADMIN_MANAGE_WORKOUT}?workoutKey=${data.workout.key}`, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const stepsData = await stepsRes.json();
      if (!stepsRes.ok || !stepsData?.success) throw new Error(stepsData?.message || "Failed to fetch steps");
      setWorkoutSteps(stepsData.workouts || []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterWorkout();
  }, [id, admin?.token]);

  const openEditor = (step = null) => {
    const initial = step ? step : getEmptyWorkoutStep(masterWorkout || {});
  
    setForm(initial);
    setError("");
    setSuccessMsg("");
    setEditingStep(step ? step._id : "new");
  
    // Reset file state
    setImageFile(null);
  
    // If editing and image exists → show preview
    if (step?.image?.url) {
      setImagePreview(`${process.env.REACT_APP_API_BASE_URL}${step.image.url}`);
    } else {
      setImagePreview("");
    }
  };

  const closeEditor = () => {
    setEditingStep(null);
    setForm(getEmptyWorkoutStep(masterWorkout || {}));
    setImageFile(null);
    setImagePreview("");
  };

  const saveWorkoutStep = async () => {
    try {
      setSaving(true);
      setError("");
  
      const payload = JSON.parse(JSON.stringify(form));
  
      if (!payload.capture) payload.capture = {};
  
      if (!payload.capture.saveKey || payload.capture.saveKey.trim() === "") {
        const clean = (v = "") =>
          String(v)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
  
        payload.capture.saveKey =
          `${clean(payload.workoutKey)}_${clean(payload.title)}_${payload.workoutIndex || "step"}`;
      }
  
      const formData = new FormData();
  
      // send JSON as string
      formData.append("data", JSON.stringify(payload));
  
      // send image file if selected
      if (imageFile) {
        formData.append("image", imageFile);
      }
  
      const res = await fetch(API_URL.ADMIN_SAVE_WORKOUT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${admin.token}`, // DO NOT set Content-Type
        },
        body: formData,
      });
  
      const data = await res.json();
  
      if (!res.ok || !data?.success)
        throw new Error(data?.message || "Failed to save step");
  
      setSuccessMsg(`Workout ${data.mode} successfully ✅`);
      closeEditor();
      fetchMasterWorkout();
  
    } catch (err) {
      setError(err.message || "Failed to save step");
    } finally {
      setSaving(false);
    }
  };

  const deleteStep = async (_id) => {
    if (!window.confirm("Are you sure you want to delete this step?")) return;
    try {
      const res = await fetch(`${API_URL.ADMIN_DELETE_WORKOUT}${_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to delete step");
      fetchMasterWorkout();
    } catch (err) {
      alert(err.message || "Failed to delete step");
    }
  };

  if (!admin) return <Navigate to="/administrator/login" />;
  if (loading)
    return <div className="flex h-screen items-center justify-center text-zinc-500">Loading master workout...</div>;

  const isComplete = workoutSteps.some((s) => s.lastStep && s.isActive);

  return (
    <div className="flex h-screen bg-zinc-100">
      <AdminSideMenu />
      <div className="flex-1 p-6 pt-16 flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => navigate("/administrator/admin/managemasterworkout")}
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 transition"
            >
              ← Back to Master Workouts
            </button>
            <div className="text-xs text-zinc-500">
              Admin / Workouts / <span className="font-semibold text-zinc-800">Build Steps</span>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-bold text-zinc-900">{masterWorkout?.title || "Workout"}</h1>
              <p className="text-sm text-zinc-600">{masterWorkout?.description || ""}</p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700">
                Key: {masterWorkout?.key}
              </span>
              <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700">
                Steps: {workoutSteps.length}
              </span>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                  isComplete
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                }`}
              >
                {isComplete ? "Complete" : "Ongoing"}
              </span>
            </div>
          </div>
        </div>

        {/* Add Step Button */}
        <button
          onClick={() => openEditor()}
          className="mb-4 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 transition"
        >
          + Add New Step
        </button>

        {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {successMsg && (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{successMsg}</div>
        )}

        {/* Steps Table */}
        <div className="overflow-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <table className="w-full table-auto">
            <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">Index</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workoutSteps.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-sm text-zinc-500">
                    No steps found.
                  </td>
                </tr>
              ) : (
                workoutSteps.map((step) => (
                  <tr key={step._id} className="border-b border-zinc-100 hover:bg-zinc-50 transition">
                    <td className="px-4 py-4 text-sm">{step.workoutIndex}</td>
                    <td className="px-4 py-4 text-sm">{step.title}</td>
                    <td className="px-4 py-4 flex gap-2">
                      <button
                        onClick={() => openEditor(step)}
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteStep(step._id)}
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 transition"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ======================= */}
        {/* Full Inline Editor */}
        {/* ======================= */}
        {editingStep && (
          <div className="rounded-3xl bg-white shadow-xl border border-zinc-200 p-6 mt-6 space-y-4">
            <h2 className="text-lg font-bold text-zinc-900 mb-4">{form._id ? "Edit Step" : "Add Step"}</h2>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-zinc-700">Title</label>
                <input
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-700">Topic</label>
                <input
                  value={form.topic}
                  onChange={e => setForm(p => ({ ...p, topic: e.target.value }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
              </div>
            </div>

            {/* Summary */}
            <div>
              <label className="text-xs font-semibold text-zinc-700">Summary</label>
              <textarea
                value={form.summary}
                onChange={e => setForm(p => ({ ...p, summary: e.target.value }))}
                rows={6}
                className="w-full rounded-xl border border-zinc-300 px-4 py-2 text-sm text-zinc-800"
              />
            </div>

            {/* Wisdom Quote */}
            <div className="space-y-2 p-3 border rounded-xl bg-zinc-50">
              <div className="font-bold text-sm">Wisdom Quote</div>
              <input
                placeholder="Quote"
                value={form.wisdomQuote.text}
                onChange={e => setForm(p => ({ ...p, wisdomQuote: { ...p.wisdomQuote, text: e.target.value } }))}
                className="w-full rounded-xl border px-4 py-2 text-sm"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  placeholder="Author"
                  value={form.wisdomQuote.author}
                  onChange={e => setForm(p => ({ ...p, wisdomQuote: { ...p.wisdomQuote, author: e.target.value } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
                <input
                  placeholder="Source"
                  value={form.wisdomQuote.source}
                  onChange={e => setForm(p => ({ ...p, wisdomQuote: { ...p.wisdomQuote, source: e.target.value } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
              </div>
            </div>

            {/* Reflection */}
            <div>
              <label className="text-xs font-semibold text-zinc-700">Reflection</label>
              <textarea
                value={form.reflection}
                onChange={e => setForm(p => ({ ...p, reflection: e.target.value }))}
                rows={6}
                className="w-full rounded-xl border border-zinc-300 px-4 py-2 text-sm text-zinc-800"
              />
            </div>

            {/* Image */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
  <label className="text-xs font-semibold text-zinc-700">
    Workout Image
  </label>

  {imagePreview && (
    <div className="w-40 rounded-xl overflow-hidden border">
      <img
        src={imagePreview}
        alt="Preview"
        className="w-full object-cover"
      />
    </div>
  )}

  <input
    type="file"
    accept="image/*"
    onChange={(e) => {
      const file = e.target.files[0];
      if (!file) return;

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }}
    className="block w-full text-sm"
  />

  <input
    placeholder="Image Caption"
    value={form.image?.caption || ""}
    onChange={(e) =>
      setForm((prev) => ({
        ...prev,
        image: {
          ...prev.image,
          caption: e.target.value,
        },
      }))
    }
    className="w-full rounded-xl border px-4 py-2 text-sm"
  />
</div>
            </div>

            {/* Action Task */}
            <div className="space-y-2 p-3 border rounded-xl bg-zinc-50">
              <div className="font-bold text-sm">Action Task</div>
              <input
                placeholder="Title"
                value={form.actionTask.title}
                onChange={e => setForm(p => ({ ...p, actionTask: { ...p.actionTask, title: e.target.value } }))}
                className="w-full rounded-xl border px-4 py-2 text-sm"
              />
              <textarea
                placeholder="Instructions"
                value={form.actionTask.instructions}
                onChange={e => setForm(p => ({ ...p, actionTask: { ...p.actionTask, instructions: e.target.value } }))}
                rows={6}
                className="w-full rounded-xl border border-zinc-300 px-4 py-2 text-sm text-zinc-800"
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.actionTask.difficulty}
                  onChange={e => setForm(p => ({ ...p, actionTask: { ...p.actionTask, difficulty: e.target.value } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                >
                  {DIFFICULTY.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <input
                  type="number"
                  placeholder="Estimated Minutes"
                  value={form.actionTask.estimatedMinutes}
                  onChange={e => setForm(p => ({ ...p, actionTask: { ...p.actionTask, estimatedMinutes: Number(e.target.value) } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
              </div>
            </div>

            {/* Capture */}
            <div className="space-y-2 p-3 border rounded-xl bg-zinc-50">
              <div className="flex items-center justify-between font-bold text-sm mb-1">
                <span>Capture</span>
                <label className="flex items-center gap-1 text-xs">
                  Enabled
                  <input
                    type="checkbox"
                    checked={form.capture.enabled}
                    onChange={e => setForm(p => ({ ...p, capture: { ...p.capture, enabled: e.target.checked } }))}
                  />
                </label>
              </div>

              <input
                placeholder="Prompt Title"
                value={form.capture.promptTitle}
                onChange={e => setForm(p => ({ ...p, capture: { ...p.capture, promptTitle: e.target.value } }))}
                className="w-full rounded-xl border px-4 py-2 text-sm"
              />
              <textarea
                placeholder="Prompt Text"
                value={form.capture.promptText}
                onChange={e => setForm(p => ({ ...p, capture: { ...p.capture, promptText: e.target.value } }))}
                rows={6}
                className="w-full rounded-xl border border-zinc-300 px-4 py-2 text-sm text-zinc-800"
              />
              <select
                value={form.capture.inputType}
                onChange={e => setForm(p => ({ ...p, capture: { ...p.capture, inputType: e.target.value } }))}
                className="w-full rounded-xl border px-4 py-2 text-sm"
              >
                <option value="text">Text</option>
                <option value="choice">Choice</option>
              </select>

              {form.capture.inputType === "choice" && (
                <div className="space-y-2">
                  {form.capture.choices.map((c, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={c}
                        onChange={e => {
                          const newChoices = [...form.capture.choices];
                          newChoices[i] = e.target.value;
                          setForm(p => ({ ...p, capture: { ...p.capture, choices: newChoices } }));
                        }}
                        className="w-full rounded-xl border px-2 py-1 text-sm"
                      />
                      <button
                        onClick={() => {
                          const newChoices = form.capture.choices.filter((_, idx) => idx !== i);
                          setForm(p => ({ ...p, capture: { ...p.capture, choices: newChoices } }));
                        }}
                        className="bg-red-500 text-white px-2 rounded"
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setForm(p => ({ ...p, capture: { ...p.capture, choices: [...p.capture.choices, ""] } }))}
                    className="mt-1 bg-green-500 text-white px-2 rounded text-xs"
                  >
                    Add Choice
                  </button>
                </div>
              )}
            </div>

            {/* Points */}
            <div className="space-y-2 p-3 border rounded-xl bg-zinc-50">
              <div className="font-bold text-sm mb-1">Points</div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  placeholder="Completed"
                  value={form.points.completed}
                  onChange={e => setForm(p => ({ ...p, points: { ...p.points, completed: Number(e.target.value) } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
                <input
                  type="number"
                  placeholder="Slipped"
                  value={form.points.slipped}
                  onChange={e => setForm(p => ({ ...p, points: { ...p.points, slipped: Number(e.target.value) } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
                <input
                  type="number"
                  placeholder="Skipped"
                  value={form.points.skipped}
                  onChange={e => setForm(p => ({ ...p, points: { ...p.points, skipped: Number(e.target.value) } }))}
                  className="w-full rounded-xl border px-4 py-2 text-sm"
                />
              </div>
            </div>

            {/* Response Buttons */}
            <div className="space-y-2 p-3 border rounded-xl bg-zinc-50">
              <div className="font-bold text-sm mb-1">Response Buttons</div>
              {form.responseButtons.map((btn, idx) => (
                <div key={idx} className="space-y-1">
                  <input
                    placeholder="Label"
                    value={btn.label}
                    onChange={e => {
                      const newBtns = [...form.responseButtons]; newBtns[idx].label = e.target.value;
                      setForm(p => ({ ...p, responseButtons: newBtns }));
                    }}
                    className="w-full rounded-xl border px-2 py-1 text-sm"
                  />
                  <input
                    placeholder="Value"
                    value={btn.value}
                    onChange={e => {
                      const newBtns = [...form.responseButtons]; newBtns[idx].value = e.target.value;
                      setForm(p => ({ ...p, responseButtons: newBtns }));
                    }}
                    className="w-full rounded-xl border px-2 py-1 text-sm"
                  />
                  <textarea
                    placeholder="Nudge Message"
                    value={btn.nudgeMessage}
                    onChange={e => {
                      const newBtns = [...form.responseButtons]; newBtns[idx].nudgeMessage = e.target.value;
                      setForm(p => ({ ...p, responseButtons: newBtns }));
                    }}
                    rows={2}
                    className="w-full rounded-xl border px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => {
                      const newBtns = form.responseButtons.filter((_, i) => i !== idx);
                      setForm(p => ({ ...p, responseButtons: newBtns }));
                    }}
                    className="bg-red-500 text-white px-2 rounded"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                onClick={() => setForm(p => ({ ...p, responseButtons: [...p.responseButtons, { label: "", value: "", nudgeMessage: "" }] }))}
                className="mt-1 bg-green-500 text-white px-2 rounded text-xs"
              >
                Add Button
              </button>
            </div>

            {/* Active & Last Step */}
            <div className="flex gap-6 items-center">
              <label className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <input type="checkbox" checked={form.lastStep} onChange={e => setForm(p => ({ ...p, lastStep: e.target.checked }))} />
                Last Step
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={closeEditor} className="rounded-xl border px-5 py-2 text-sm font-semibold hover:bg-zinc-100">Cancel</button>
              <button disabled={saving} onClick={saveWorkoutStep} className={`rounded-xl px-5 py-2 text-sm font-semibold text-white ${saving ? "bg-zinc-300 cursor-not-allowed" : "bg-zinc-900 hover:bg-zinc-800"}`}>
                {saving ? "Saving..." : form._id ? "Update Step" : "Create Step"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};